import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useCameraCapture } from '../../hooks/useCameraCapture';
import { usePhotoUpload } from '../../hooks/usePhotoUpload';
import { CapturedPhoto } from '../../types/submission-workflow';
import { ImageCompressionService } from '../../services/imageCompressionService';

interface PhotoCaptureStepProps {
  photos: CapturedPhoto[];
  onAddPhoto: (photo: CapturedPhoto) => void;
  onRemovePhoto: (photoId: string) => void;
  maxPhotos?: number;
  maxFileSize?: number;
}

export const PhotoCaptureStep: React.FC<PhotoCaptureStepProps> = ({
  photos,
  onAddPhoto,
  onRemovePhoto,
  maxPhotos = 5,
  maxFileSize = 10 * 1024 * 1024 // 10MB
}) => {
  const [activeTab, setActiveTab] = useState<'camera' | 'upload'>('camera');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const activePointersRef = useRef<Map<number, PointerEvent>>(new Map());
  const lastPinchDistanceRef = useRef<number | null>(null);
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  
  const { 
    uploadPhoto, 
    uploadProgress, 
    uploadErrors,
    clearError: clearUploadError 
  } = usePhotoUpload();

  const {
    camera,
    isCameraSupported,
    videoRef,
    canvasRef,
    startCamera,
    stopCamera,
    switchCamera,
    capturePhoto,
    setZoom,
    zoomIn,
    zoomOut,
    clearError: clearCameraError
  } = useCameraCapture({ maxWidth: 1920, maxHeight: 1920, initialZoom: 1 });

  useEffect(() => {
    const nav = navigator as Navigator & { msMaxTouchPoints?: number };
    const hasTouchSupport = 'ontouchstart' in window || nav.maxTouchPoints > 0 || (nav.msMaxTouchPoints ?? 0) > 0;
    setIsTouchDevice(Boolean(hasTouchSupport));
  }, []);

  const resetPinchTracking = useCallback(() => {
    activePointersRef.current.clear();
    lastPinchDistanceRef.current = null;
  }, []);

  useEffect(() => {
    if (!camera.isActive) {
      resetPinchTracking();
    }
  }, [camera.isActive, resetPinchTracking]);

  const handlePointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (!camera.supportsZoom || !isTouchDevice || event.pointerType !== 'touch') {
      return;
    }
    event.preventDefault();
    activePointersRef.current.set(event.pointerId, event.nativeEvent);
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {
      // Pointer capture may not be supported; safe to ignore
    }

    if (activePointersRef.current.size === 2) {
      const [first, second] = Array.from(activePointersRef.current.values());
      lastPinchDistanceRef.current = Math.hypot(first.clientX - second.clientX, first.clientY - second.clientY);
    }
  }, [camera.supportsZoom, isTouchDevice]);

  const handlePointerMove = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (!camera.supportsZoom || !isTouchDevice || event.pointerType !== 'touch') {
      return;
    }
    if (!activePointersRef.current.has(event.pointerId)) {
      return;
    }

    event.preventDefault();
    activePointersRef.current.set(event.pointerId, event.nativeEvent);

    if (activePointersRef.current.size < 2) {
      return;
    }

    const [first, second] = Array.from(activePointersRef.current.values());
    const currentDistance = Math.hypot(first.clientX - second.clientX, first.clientY - second.clientY);
    const previousDistance = lastPinchDistanceRef.current;

    if (!previousDistance) {
      lastPinchDistanceRef.current = currentDistance;
      return;
    }

    const distanceDelta = currentDistance - previousDistance;
    if (Math.abs(distanceDelta) < 4) {
      return;
    }

    const zoomRange = camera.maxZoom - camera.minZoom || 1;
    const deltaRatio = distanceDelta / 200;
    const nextZoom = camera.zoom + deltaRatio * zoomRange;
    const clampedZoom = Math.min(Math.max(nextZoom, camera.minZoom), camera.maxZoom);

    lastPinchDistanceRef.current = currentDistance;
    void setZoom(clampedZoom);
  }, [camera.maxZoom, camera.minZoom, camera.supportsZoom, camera.zoom, isTouchDevice, setZoom]);

  const handlePointerUp = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (event.pointerType === 'touch' && activePointersRef.current.has(event.pointerId)) {
      activePointersRef.current.delete(event.pointerId);
      try {
        event.currentTarget.releasePointerCapture(event.pointerId);
      } catch {
        // Ignore release failures; pointer capture not guaranteed
      }
    }

    if (activePointersRef.current.size < 2) {
      lastPinchDistanceRef.current = null;
    }
  }, []);

  const zoomLabel = Number.isFinite(camera.zoom) ? camera.zoom.toFixed(1) : '1.0';

  const handleZoomInClick = useCallback(() => {
    if (!camera.supportsZoom) return;
    void zoomIn();
  }, [camera.supportsZoom, zoomIn]);

  const handleZoomOutClick = useCallback(() => {
    if (!camera.supportsZoom) return;
    void zoomOut();
  }, [camera.supportsZoom, zoomOut]);

  // Handle camera tab activation
  const handleCameraTabClick = useCallback(async () => {
    setActiveTab('camera');
    if (!camera.isActive && isCameraSupported) {
      await startCamera();
    }
  }, [camera.isActive, isCameraSupported, startCamera]);

  // Handle upload tab activation
  const handleUploadTabClick = useCallback(() => {
    setActiveTab('upload');
    if (camera.isActive) {
      stopCamera();
    }
  }, [camera.isActive, stopCamera]);

  // Handle camera capture
  const handleCameraCapture = useCallback(async () => {
    const photo = await capturePhoto();
    if (photo) {
      // Add photo immediately with blob URL
      onAddPhoto(photo);
      
      // Upload to Firebase Storage in background
      const uploadResult = await uploadPhoto(photo);
      if (uploadResult.success && uploadResult.photo) {
        // Update photo with Firebase Storage URL and mark as uploaded
        onAddPhoto({ ...uploadResult.photo, isUploaded: true });
      }
    }
  }, [capturePhoto, onAddPhoto, uploadPhoto]);

  // Handle file upload
  const handleFileUpload = useCallback(async (files: FileList) => {
    const validFiles = Array.from(files).filter(file => {
      const isValidType = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(file.type);
      const isValidSize = file.size <= maxFileSize;
      return isValidType && isValidSize;
    });

    if (validFiles.length !== files.length) {
      alert('Some files were rejected. Only JPEG, PNG, and WebP files under 10MB are allowed.');
    }

    // Process each valid file
    for (const file of validFiles) {
      if (photos.length >= maxPhotos) {
        alert(`Maximum ${maxPhotos} photos allowed`);
        break;
      }

      try {
        // Compress the image before processing
        console.log(`Compressing uploaded file ${file.name}...`);
        const compressionResult = await ImageCompressionService.compressImage(file, {
          maxWidthOrHeight: 1920,
          maxSizeMB: 0.8, // 800KB
          quality: 0.85,
          fileType: 'jpeg',
          preserveExif: true
        });

        const fileToProcess = compressionResult.success ? compressionResult.file : file;
        
        if (compressionResult.success) {
          console.log(`Compressed ${file.name}: ${compressionResult.originalSize} → ${compressionResult.compressedSize} bytes (${Math.round(compressionResult.compressionRatio * 100)}% reduction)`);
        } else {
          console.warn(`Compression failed for ${file.name}, using original:`, compressionResult.error);
        }

        // Create image dimensions from compressed file
        const dimensions = await new Promise<{ width: number; height: number }>((resolve) => {
          const img = new Image();
          img.onload = () => {
            resolve({ width: img.width, height: img.height });
          };
          img.src = URL.createObjectURL(fileToProcess);
        });

        // Create captured photo object with compressed file blob URL
        const timestamp = Date.now();
        const photoId = `photo_${timestamp}_${Math.random().toString(36).substring(2)}`;
        const url = URL.createObjectURL(fileToProcess);

        const capturedPhoto: CapturedPhoto = {
          id: photoId,
          filename: file.name, // Keep original filename
          url,
          size: fileToProcess.size, // Use compressed size
          dimensions,
          source: {
            type: 'upload',
            timestamp: new Date()
          },
          capturedAt: new Date(),
          isUploaded: false
        };

        // Add photo immediately with compressed blob URL
        onAddPhoto(capturedPhoto);

        // Upload compressed photo to Firebase Storage in background
        const uploadResult = await uploadPhoto(capturedPhoto);
        if (uploadResult.success && uploadResult.photo) {
          // Update photo with Firebase Storage URL
          onAddPhoto({ ...uploadResult.photo, isUploaded: true });
        }

      } catch (error) {
        console.error('Error processing file:', error);
      }
    }
  }, [photos.length, maxPhotos, maxFileSize, onAddPhoto]);

  // Handle drag and drop
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileUpload(files);
    }
  }, [handleFileUpload]);

  return (
    <div className="space-y-6">
      {/* Step Header */}
      <div className="text-center">
        <h2 className="text-2xl font-bold text-urban mb-2">Capture Photos</h2>
        <p className="text-urban-300">
          Take photos or upload images of your curbside treasure ({photos.length}/{maxPhotos})
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="flex bg-teddy-200 rounded-lg p-1">
        <button
          onClick={handleCameraTabClick}
          className={`flex-1 py-2 px-4 rounded-md font-medium transition-colors ${
            activeTab === 'camera'
              ? 'bg-white text-urban shadow-sm'
              : 'text-urban-300 hover:text-urban'
          }`}
          disabled={!isCameraSupported}
        >
          📷 Camera
        </button>
        <button
          onClick={handleUploadTabClick}
          className={`flex-1 py-2 px-4 rounded-md font-medium transition-colors ${
            activeTab === 'upload'
              ? 'bg-white text-urban shadow-sm'
              : 'text-urban-300 hover:text-urban'
          }`}
        >
          📁 Upload
        </button>
      </div>

      {/* Camera Interface */}
      {activeTab === 'camera' && (
        <div className="space-y-4">
          {!isCameraSupported ? (
            <div className="text-center p-8 bg-teddy-100 rounded-lg">
              <p className="text-urban-300">Camera is not supported on this device</p>
              <p className="text-sm text-urban-200 mt-2">Please use the upload option instead</p>
            </div>
          ) : (
            <>
              {/* Camera Error */}
              {camera.error && (
                <div className="bg-rust-50 border border-rust-200 rounded-lg p-4">
                  <p className="text-rust-800">{camera.error}</p>
                  <button
                    onClick={clearCameraError}
                    className="mt-2 text-sm text-rust-600 hover:text-rust-800 underline"
                  >
                    Dismiss
                  </button>
                </div>
              )}

              {/* Camera Preview */}
              <div
                className={`relative bg-black rounded-lg overflow-hidden aspect-video ${camera.supportsZoom && isTouchDevice ? 'touch-none' : ''}`}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onPointerCancel={handlePointerUp}
                onPointerLeave={handlePointerUp}
              >
                <video
                  ref={videoRef}
                  className="w-full h-full object-cover"
                  playsInline
                  muted
                />
                <canvas ref={canvasRef} className="hidden" />
                
                {!camera.isActive && !camera.error && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <button
                      onClick={startCamera}
                      className="bg-cardboard text-white px-6 py-3 rounded-lg font-medium hover:bg-cardboard-600 transition-colors"
                    >
                      Start Camera
                    </button>
                  </div>
                )}
              </div>

              {/* Camera Controls */}
              {camera.isActive && (
                <div className="space-y-3">
                  {camera.supportsZoom && (
                    <div className="flex flex-col items-center gap-2">
                      <div className="text-sm text-urban-300">
                        {isTouchDevice
                          ? `Pinch to zoom (current ${zoomLabel}x)`
                          : `Zoom level ${zoomLabel}x`}
                      </div>
                      {!isTouchDevice && (
                        <div className="flex items-center gap-3">
                          <button
                            onClick={handleZoomOutClick}
                            className="w-10 h-10 rounded-full bg-teddy-200 text-2xl leading-none flex items-center justify-center hover:bg-teddy-300"
                            title="Zoom out"
                          >
                            −
                          </button>
                          <button
                            onClick={handleZoomInClick}
                            className="w-10 h-10 rounded-full bg-teddy-200 text-2xl leading-none flex items-center justify-center hover:bg-teddy-300"
                            title="Zoom in"
                          >
                            +
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="flex justify-center items-center space-x-4">
                    <button
                      onClick={switchCamera}
                      className="p-3 bg-teddy-200 rounded-full hover:bg-teddy-300 transition-colors"
                      title="Switch Camera"
                    >
                      🔄
                    </button>
                    
                    <button
                      onClick={handleCameraCapture}
                      disabled={photos.length >= maxPhotos}
                      className="w-16 h-16 bg-cardboard text-white rounded-full hover:bg-cardboard-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center text-2xl"
                      title="Capture Photo"
                    >
                      📷
                    </button>
                    
                    <button
                      onClick={stopCamera}
                      className="p-3 bg-rust text-white rounded-full hover:bg-rust-600 transition-colors"
                      title="Stop Camera"
                    >
                      ⏹️
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Upload Interface */}
      {activeTab === 'upload' && (
        <div className="space-y-4">
          {/* File Input */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/jpeg,image/jpg,image/png,image/webp"
            onChange={(e) => e.target.files && handleFileUpload(e.target.files)}
            className="hidden"
          />

          {/* Drag and Drop Area */}
          <div
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            className="border-2 border-dashed border-urban-300 rounded-lg p-8 text-center hover:border-cardboard hover:bg-teddy-50 transition-colors cursor-pointer"
            onClick={() => fileInputRef.current?.click()}
          >
            <div className="space-y-2">
              <div className="text-4xl">📁</div>
              <p className="text-urban font-medium">Click to select images or drag and drop</p>
              <p className="text-sm text-urban-300">
                JPEG, PNG, WebP up to 10MB each (max {maxPhotos} photos)
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Photo Grid */}
      {photos.length > 0 && (
        <div className="space-y-4">
          <h3 className="font-medium text-urban">Captured Photos ({photos.length})</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {photos.map((photo) => (
              <div key={photo.id} className="relative group">
                <img
                  src={photo.url}
                  alt={`Captured ${photo.source.type}`}
                  className="w-full h-32 object-cover rounded-lg"
                />
                
                {/* Photo Info Overlay */}
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-200 rounded-lg flex items-center justify-center">
                  <button
                    onClick={() => onRemovePhoto(photo.id)}
                    className="opacity-0 group-hover:opacity-100 bg-rust text-white rounded-full w-8 h-8 flex items-center justify-center hover:bg-rust-600 transition-all"
                    title="Remove photo"
                  >
                    ×
                  </button>
                </div>

                {/* Upload Progress */}
                {uploadProgress[photo.id] !== undefined && (
                  <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-75 text-white text-xs p-2 rounded-b-lg">
                    <div className="flex items-center justify-between">
                      <span>Uploading to storage...</span>
                      <span>{uploadProgress[photo.id]}%</span>
                    </div>
                    <div className="w-full bg-urban-300 rounded-full h-1 mt-1">
                      <div
                        className="bg-treasure h-1 rounded-full transition-all duration-300"
                        style={{ width: `${uploadProgress[photo.id]}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Upload Error */}
                {uploadErrors[photo.id] && (
                  <div className="absolute bottom-0 left-0 right-0 bg-rust bg-opacity-90 text-white text-xs p-2 rounded-b-lg">
                    <div className="flex items-center justify-between">
                      <span>Upload failed: {uploadErrors[photo.id]}</span>
                      <button
                        onClick={() => clearUploadError(photo.id)}
                        className="text-white hover:text-rust-200"
                      >
                        ×
                      </button>
                    </div>
                  </div>
                )}

                {/* Upload Status Badge */}
                {photo.isUploaded && (
                  <div className="absolute top-2 right-2 bg-verge text-white text-xs px-2 py-1 rounded">
                    ✓ Uploaded
                  </div>
                )}

                {/* Photo Source Badge */}
                <div className="absolute top-2 left-2 bg-black bg-opacity-75 text-white text-xs px-2 py-1 rounded">
                  {photo.source.type === 'camera' ? '📷' : '📁'}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Help Text */}
      <div className="bg-verge-50 border border-verge-200 rounded-lg p-4">
        <h4 className="font-medium text-verge-800 mb-2">📝 Photo Tips</h4>
        <ul className="text-sm text-verge-700 space-y-1">
          <li>• Take clear, well-lit photos of the items</li>
          <li>• Show the items from multiple angles if possible</li>
          <li>• Avoid including people in your photos</li>
        </ul>
      </div>
    </div>
  );
};
